/**
 * Exportación a CSV.
 *
 * Respeta los filtros y las columnas visibles (ERP-UX.md §3): exportar más
 * de lo que se está viendo sorprende, y exportar menos hace que la gente
 * deje de fiarse y copie a mano.
 *
 * Se escribe a mano en lugar de traer una librería: son cuarenta líneas,
 * y el §117 pide no añadir dependencias sin justificar.
 */

/**
 * Escapa un valor.
 *
 * El separador es el punto y coma porque es lo que espera Excel en
 * configuración regional española; con coma, una sola celda con decimales
 * parte la fila entera.
 */
function cell(value: unknown): string {
  if (value === null || value === undefined) return '';

  const text = String(value);

  // Una celda que empieza por `=`, `+`, `-` o `@` la ejecuta Excel como
  // fórmula al abrir el fichero. Con datos que vienen de un formulario,
  // eso es inyección de fórmulas: se neutraliza anteponiendo un apóstrofo.
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return /[";\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export interface CsvColumn<Row> {
  id: string;
  label: string;
  /** Qué se exporta de esta fila. Puede diferir de lo que se pinta. */
  value: (row: Row) => unknown;
}

export function toCsv<Row>(columns: readonly CsvColumn<Row>[], rows: readonly Row[]): string {
  const lines = [columns.map((c) => cell(c.label)).join(';')];

  for (const row of rows) {
    lines.push(columns.map((c) => cell(c.value(row))).join(';'));
  }

  // El BOM no es decorativo: sin él, Excel abre el fichero en la
  // codificación del sistema y «Añó» sale como «AÃ±Ã³».
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/** Lanza la descarga en el navegador. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Sin revocar, cada exportación deja el fichero entero en memoria hasta
  // recargar la página. En una jornada de oficina eso se nota.
  URL.revokeObjectURL(url);
}
